import { SystemTypes } from "spark-core-dx/services";
import {
  asArray,
  getId,
  readValue
} from "../wizard/resourceConfigWizardUtils";
import {
  createClientGuid,
  DESTINATION_KIND_EXTERNAL_SYSTEM,
  DESTINATION_KIND_FILE_STORAGE,
  DESTINATION_KIND_WAREHOUSE,
  getDeploymentDisplayName,
  getDeploymentRecordId,
  getDestinationKind,
  getDestinationSystemTypeValue,
  getEntityObjectDisplayName,
  getEntityObjectRecordId
} from "../wizard/steps/destinationSetupStepUtils";

export const DESTINATION_SCHEDULE_FORM_GROUP_ID = "resourceDestinationScheduleV1";
export const CORE_SELECTDATA_TIMEZONES_OPERATION = "SelectDataTimeZones";
export const CONVOKE_SELECTDATA_RUNTIME_CAPACITY_GRANT_OPERATION = "SelectDataRuntimeCapacityGrant";
export const CONVOKE_SELECTDATA_RUNTIME_CAPACITY_POOL_PROFILE_OPERATION = "SelectDataRuntimeCapacityPoolProfile";
export const SCHEDULE_DECISION_CONFIGURE = "Configure";
export const SCHEDULE_DECISION_NONE = "NoSchedule";

export const DESTINATION_SCHEDULE_MODE_INTERVAL = 1;
export const DESTINATION_SCHEDULE_MODE_CALENDAR = 2;
export const DESTINATION_SCHEDULE_FREQUENCY_DAILY = 1;
export const DESTINATION_SCHEDULE_FREQUENCY_WEEKLY = 2;
export const DESTINATION_SCHEDULE_FREQUENCY_MONTHLY = 3;
export const DESTINATION_SCHEDULE_RUN_TIME_MODE_PRESET = 1;
export const DESTINATION_SCHEDULE_RUN_TIME_MODE_MANUAL = 2;

export const SCHEDULE_MODE_OPTIONS = [
  { Text: "Interval", Value: DESTINATION_SCHEDULE_MODE_INTERVAL },
  { Text: "Calendar", Value: DESTINATION_SCHEDULE_MODE_CALENDAR }
];

export const CALENDAR_FREQUENCY_OPTIONS = [
  { Text: "Daily", Value: DESTINATION_SCHEDULE_FREQUENCY_DAILY, SortOrder: 1 },
  { Text: "Weekly", Value: DESTINATION_SCHEDULE_FREQUENCY_WEEKLY, SortOrder: 2 },
  { Text: "Monthly", Value: DESTINATION_SCHEDULE_FREQUENCY_MONTHLY, SortOrder: 3 }
];

export const RUN_TIME_ENTRY_MODE_OPTIONS = [
  { Text: "30-Minute Intervals", Value: DESTINATION_SCHEDULE_RUN_TIME_MODE_PRESET },
  { Text: "Manual Mode", Value: DESTINATION_SCHEDULE_RUN_TIME_MODE_MANUAL }
];

export const LOCAL_RUN_TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const totalMinutes = index * 30;
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const minutes = String(totalMinutes % 60).padStart(2, "0");
  const value = `${hours}:${minutes}`;
  return { Text: value, Value: value, SortOrder: index };
});

export const WEEKDAY_OPTIONS = [
  { Text: "Sunday", Value: 0 },
  { Text: "Monday", Value: 1 },
  { Text: "Tuesday", Value: 2 },
  { Text: "Wednesday", Value: 3 },
  { Text: "Thursday", Value: 4 },
  { Text: "Friday", Value: 5 },
  { Text: "Saturday", Value: 6 }
];

export const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => {
  const day = index + 1;
  return { Text: String(day), Value: day, SortOrder: day };
});

export const MONTHLY_ORDINAL_OPTIONS = [
  { Text: "First", Value: 1 },
  { Text: "Second", Value: 2 },
  { Text: "Third", Value: 3 },
  { Text: "Fourth", Value: 4 },
  { Text: "Last", Value: 5 }
];

export const EXTERNAL_OPERATION_OPTIONS = [
  { Text: "Sync", Value: 1 },
  { Text: "Validate", Value: 2 },
  { Text: "Preview", Value: 3 },
  { Text: "Discover Dirty", Value: 4 },
  { Text: "Load Dirty Queue", Value: 5 }
];

export const WAREHOUSE_STEP_OPTIONS = [
  { Text: "Set Deployment Dates/Ids (Only)", Value: "ResetOnly" },
  { Text: "Update Deployment Dates/Ids Records (Only)", Value: "UpdateEntityDateInternalId" },
  { Text: "Post Load (Only)", Value: "PostLoadOnly" },
  { Text: "Source Object Key Sync (Partial)", Value: "SourceObjectKeySync" },
  { Text: "Source Object Key Sync (Full)", Value: "SourceObjectKeySyncWithReload" },
  { Text: "Entity Master Sync (Partial)", Value: "EntityMasterRecordPartialSync" },
  { Text: "Entity Master Sync (Full)", Value: "EntityMasterRecordFullSync" },
  { Text: "Partial Warehouse Sync (Skip Keys)", Value: "PartialWarehouseSyncNoKeys" },
  { Text: "Partial Warehouse Sync (Skip Post Load)", Value: "PartialWarehouseSyncNPL" },
  { Text: "Partial Warehouse Sync", Value: "PartialWarehouseSync" },
  { Text: "Full Warehouse Sync (Skip Keys)", Value: "FullWarehouseSyncNoKeys" },
  { Text: "Full Warehouse Sync (Skip Post Load)", Value: "FullWarehouseSyncNPL" },
  { Text: "Full Warehouse Sync", Value: "FullWarehouseSync" }
];

const targetTypeByDestinationKind = {
  [DESTINATION_KIND_WAREHOUSE]: 1,
  [DESTINATION_KIND_FILE_STORAGE]: 2,
  [DESTINATION_KIND_EXTERNAL_SYSTEM]: 3
};

export const fieldMetadata = ({
  fieldName,
  displayName,
  fieldType = SystemTypes.UIFieldType.TextBox,
  options = [],
  description = "",
  isRequired = false,
  format = "",
  dataOperation = "",
  dataOperationApp = "",
  dataOperationFilterProperty = ""
}) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: fieldType,
  Description: description,
  IsRequired: isRequired,
  Format: format,
  DataJson: options,
  DataOperation: dataOperation,
  DataOperationApp: dataOperationApp,
  DataOperationFilterProperty: dataOperationFilterProperty
});

export const fieldValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return readValue(value, "Value", "value", "Text", "text") ?? "";
  }
  return value ?? "";
};

export const normalizeNumber = (value, fallback = 0) => {
  const raw = fieldValue(value);
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(fieldValue(entry) ?? "").trim()).filter(Boolean);
  }
  const text = String(fieldValue(value) ?? "").trim();
  if (!text) return [];
  if (text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return normalizeStringArray(parsed);
    } catch {
      return [];
    }
  }
  return text.split(",").map((entry) => entry.trim()).filter(Boolean);
};

export const normalizeNumberArray = (value) =>
  normalizeStringArray(value)
    .map((entry) => Number(entry))
    .filter((entry) => Number.isFinite(entry));

export const normalizeGuid = (value) => String(value || "").trim().toLowerCase();

export const getDestinationRecordId = (destination) =>
  readValue(destination, "SchemaDestinationId", "schemaDestinationId", "UniqueId", "uniqueId") || getId(destination) || "";

export const buildDestinationScheduleScopeKey = (deploymentId, entityObjectIds = []) => {
  const deploymentKey = String(deploymentId || "").trim();
  const entityIds = asArray(entityObjectIds)
    .map((entry) => String(entry || "").trim())
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));
  return `deployment:${deploymentKey}|entities:${entityIds.length ? entityIds.join(",") : "all"}`;
};

export const buildDestinationScheduleScopeDisplayName = ({ deployment, entityObjects = [], entityObjectIds = [], defaults = {} }) => {
  const deploymentName = getDeploymentDisplayName(deployment, defaults) || "Deployment";
  const selectedIds = new Set(asArray(entityObjectIds).map(normalizeGuid).filter(Boolean));
  if (!selectedIds.size) return `${deploymentName}: all entities`;
  const names = asArray(entityObjects)
    .filter((entityObject) => selectedIds.has(normalizeGuid(getEntityObjectRecordId(entityObject))))
    .map(getEntityObjectDisplayName)
    .filter(Boolean);
  return `${deploymentName}: ${names.length ? names.join(", ") : `${selectedIds.size} selected entity object(s)`}`;
};

export const getScheduleRecordId = (schedule) =>
  readValue(schedule, "DestinationScheduleId", "destinationScheduleId", "UniqueId", "uniqueId") || getId(schedule) || "";

export const getScheduleRowKey = (schedule, index = 0) =>
  readValue(schedule, "_tempKey") || getScheduleRecordId(schedule) || `${readValue(schedule, "ScopeKey", "scopeKey") || "schedule"}-${index}`;

export const getScheduleScopeKey = (schedule) =>
  readValue(schedule, "ScopeKey", "scopeKey") || buildDestinationScheduleScopeKey(
    readValue(schedule, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId"),
    readValue(schedule, "EntityObjectIds", "entityObjectIds") || []
  );

export const isScheduleActive = (schedule) =>
  readValue(schedule, "IsDeleted", "isDeleted") !== true
  && readValue(schedule, "IsActive", "isActive") !== false;

const getOptionText = (options, value) =>
  options.find((option) => String(option.Value) === String(value))?.Text || String(value ?? "");

const formatListPreview = (values = [], maxItems = 3) => {
  const normalized = normalizeStringArray(values);
  if (!normalized.length) return "";
  const preview = normalized.slice(0, maxItems).join(", ");
  const remaining = normalized.length - maxItems;
  return remaining > 0 ? `${preview} + ${remaining} more` : preview;
};

const formatRunTimeSummary = (schedule) =>
  formatListPreview(readValue(schedule, "LocalRunTimes", "localRunTimes")) || "no run time set";

const formatMonthlyWeekdayRule = (rule) => {
  const ordinal = getOptionText(MONTHLY_ORDINAL_OPTIONS, readValue(rule, "Ordinal", "ordinal"));
  const weekday = getOptionText(WEEKDAY_OPTIONS, readValue(rule, "DayOfWeek", "dayOfWeek"));
  return `${ordinal.toLowerCase()} ${weekday}`;
};

const formatMonthlyScheduleSummary = (schedule) => {
  const monthlyDays = normalizeNumberArray(readValue(schedule, "MonthlyDays", "monthlyDays"))
    .sort((left, right) => left - right)
    .map(String);
  const monthlyDayText = monthlyDays.length ? `days ${formatListPreview(monthlyDays)}` : "";
  const monthlyRuleText = asArray(readValue(schedule, "MonthlyWeekdayRules", "monthlyWeekdayRules"))
    .map(formatMonthlyWeekdayRule)
    .filter(Boolean);
  return formatListPreview([monthlyDayText, ...monthlyRuleText].filter(Boolean)) || "monthly schedule";
};

export const buildDestinationScheduleTimingSummary = (schedule) => {
  const scheduleMode = Number(readValue(schedule, "ScheduleMode", "scheduleMode") || DESTINATION_SCHEDULE_MODE_INTERVAL);
  if (scheduleMode === DESTINATION_SCHEDULE_MODE_INTERVAL) {
    const minutes = Number(readValue(schedule, "RecurrenceIntervalMinutes", "recurrenceIntervalMinutes") || 60);
    return `Interval: every ${minutes} minute${minutes === 1 ? "" : "s"}`;
  }

  const runTimes = formatRunTimeSummary(schedule);
  const frequency = Number(readValue(schedule, "CalendarFrequency", "calendarFrequency") || DESTINATION_SCHEDULE_FREQUENCY_DAILY);
  if (frequency === DESTINATION_SCHEDULE_FREQUENCY_WEEKLY) {
    const weeklyDays = normalizeNumberArray(readValue(schedule, "WeeklyDays", "weeklyDays"))
      .sort((left, right) => left - right)
      .map((day) => getOptionText(WEEKDAY_OPTIONS, day));
    const weeklyText = weeklyDays.length ? `weekly on ${formatListPreview(weeklyDays)}` : "weekly";
    return `Calendar: ${weeklyText} at ${runTimes}`;
  }
  if (frequency === DESTINATION_SCHEDULE_FREQUENCY_MONTHLY) {
    return `Calendar: monthly on ${formatMonthlyScheduleSummary(schedule)} at ${runTimes}`;
  }

  const intervalDays = Number(readValue(schedule, "DailyIntervalDays", "dailyIntervalDays") || 1);
  const dailyText = intervalDays > 1 ? `every ${intervalDays} days` : "daily";
  return `Calendar: ${dailyText} at ${runTimes}`;
};

export const buildDestinationScheduleDraft = ({
  destination,
  deployment,
  entityObjects,
  entityObjectIds = [],
  defaults = {},
  clientResourceId = "",
  resourceConfigurationId = "",
  scheduleName = "Schedule"
}) => {
  const destinationKind = getDestinationKind(defaults);
  const destinationId = getDestinationRecordId(destination);
  const deploymentId = getDeploymentRecordId(deployment);
  const scopeKey = buildDestinationScheduleScopeKey(deploymentId, entityObjectIds);
  const scopeDisplayName = buildDestinationScheduleScopeDisplayName({ deployment, entityObjects, entityObjectIds, defaults });
  return {
    _tempKey: `destination-schedule-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    UniqueId: createClientGuid(),
    IsNew: true,
    IsActive: true,
    IsDeleted: false,
    Name: scheduleName,
    Description: "",
    ClientResourceId: clientResourceId,
    ResourceConfigurationId: resourceConfigurationId,
    TargetType: targetTypeByDestinationKind[destinationKind] || 1,
    SchemaDestinationId: destinationId,
    SchemaDestinationDeploymentId: deploymentId,
    DestinationSystemType: getDestinationSystemTypeValue(defaults),
    EntityObjectIds: asArray(entityObjectIds),
    ScopeKey: scopeKey,
    ScopeDisplayName: scopeDisplayName,
    EnableSchedule: false,
    ScheduleMode: DESTINATION_SCHEDULE_MODE_INTERVAL,
    RecurrenceIntervalMinutes: 60,
    TimeZoneId: "Eastern Standard Time|America/New_York",
    CalendarFrequency: DESTINATION_SCHEDULE_FREQUENCY_DAILY,
    LocalRunTimeEntryMode: DESTINATION_SCHEDULE_RUN_TIME_MODE_PRESET,
    LocalRunTimes: ["00:00"],
    DailyIntervalDays: 1,
    WeeklyDays: [1],
    MonthlyDays: [1],
    UseLastDayForShortMonths: true,
    MonthlyWeekdayRules: [],
    WarehouseSyncStep: "PartialWarehouseSync",
    WarehouseResetFromDate: "",
    WarehouseResetFromValue: "",
    WarehouseForceSync: false,
    WarehouseForceReconcile: false,
    WarehouseMultiThread: false,
    FileStorageFullSync: false,
    FileStorageSingleThread: true,
    ExternalSystemOperation: 1,
    ExternalSystemTake: 250
  };
};

export const resolveScheduleDecision = (schedules = [], decision = SCHEDULE_DECISION_NONE) => {
  const activeSchedules = asArray(schedules).filter(isScheduleActive);
  return activeSchedules.length ? (decision || SCHEDULE_DECISION_CONFIGURE) : SCHEDULE_DECISION_NONE;
};

export const buildScheduleStepValues = (schedules = [], decision = SCHEDULE_DECISION_NONE) => {
  const activeSchedules = asArray(schedules).filter(isScheduleActive);
  const ids = activeSchedules.map(getScheduleRecordId).filter(Boolean);
  const enabledCount = activeSchedules.filter((schedule) => readValue(schedule, "EnableSchedule", "enableSchedule") === true).length;
  return {
    ScheduleDecision: resolveScheduleDecision(activeSchedules, decision),
    DestinationScheduleIds: ids,
    DestinationScheduleCount: activeSchedules.length,
    EnabledDestinationScheduleCount: enabledCount
  };
};

export const validateDestinationSchedules = (schedules = []) => {
  const seen = new Map();
  for (const schedule of asArray(schedules).filter(isScheduleActive)) {
    const scopeKey = getScheduleScopeKey(schedule);
    if (!scopeKey) return "Each schedule needs a destination scope.";
    if (seen.has(scopeKey)) return "Each destination scope can only have one active schedule.";
    seen.set(scopeKey, true);
    if (!String(readValue(schedule, "Name", "name") || "").trim()) return "Each schedule needs a name.";
    const mode = Number(readValue(schedule, "ScheduleMode", "scheduleMode"));
    if (mode === DESTINATION_SCHEDULE_MODE_INTERVAL && Number(readValue(schedule, "RecurrenceIntervalMinutes", "recurrenceIntervalMinutes")) < 5) {
      return "Interval schedules require at least 5 minutes.";
    }
    if (mode === DESTINATION_SCHEDULE_MODE_CALENDAR && !normalizeStringArray(readValue(schedule, "LocalRunTimes", "localRunTimes")).length) {
      return "Calendar schedules require at least one local run time.";
    }
  }
  return "";
};
